import { FC, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { AutocompleteRenderInputParams, FormInputText, InputLabel, Select, Text } from '@hopskipdrive/react-components';

import { useFlags } from '../../../contexts/LDContext';
import { SelectAccountLocation } from '../SelectAccountLocation';

interface RoleFormFieldProps {
  roleOptions: { value: string; label: string }[];
  roleDescriptionMap: Map<string | null, string>;
  isFetching: boolean;
  defaultRole?: Nullable<string>;
  addOnsiteStaffForm?: boolean;
}

export const RoleFormField: FC<RoleFormFieldProps> = ({
  roleOptions,
  roleDescriptionMap,
  isFetching,
  defaultRole,
  addOnsiteStaffForm = false,
}) => {
  const {
    control,
    watch,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext();
  const selected = watch('role');
  const { enableDailyQueueAssignedLocations } = useFlags();

  // Reset location fields when role changes
  useEffect(() => {
    if (selected === 'onsite_staff') {
      // Do not reset, keep current value
      return;
    }
    // Reset both fields to null/empty when role changes away from onsite_staff
    setValue('accountLocations', []);
    setValue('defaultAccountLocation', null);
  }, [selected, setValue]);

  useEffect(() => {
    if ((roleOptions.length, defaultRole)) {
      setValue('role', defaultRole);
    }
  }, [roleOptions.length, defaultRole]);

  const shouldShowLocationField =
    !!roleOptions.length && selected === 'onsite_staff' && (defaultRole !== 'onsite_staff' || addOnsiteStaffForm);

  return (
    <>
      <InputLabel fieldtype="required" htmlFor="role" id="role-label">
        Role
      </InputLabel>
      <Controller
        control={control}
        name="role"
        render={({ field: { value, onChange } }) => {
          return (
            <Select
              id="role"
              options={roleOptions}
              value={roleOptions.length ? value : null}
              setValue={(newValue) => {
                onChange(newValue ? newValue : null);
              }}
              isLoading={isFetching}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <FormInputText
                  {...params}
                  variant="outlined"
                  fullWidth
                  data-testid="role"
                  placeholder="Select"
                  error={!!errors.role}
                  helperText={errors.role?.message as string}
                  inputProps={{
                    ...params.inputProps,
                    'aria-labelledby': 'role-label',
                  }}
                />
              )}
            />
          );
        }}
      />

      {roleDescriptionMap.size > 0 && (
        <Text className="role-info-box" variant="labelS">
          {roleDescriptionMap.get(selected)}
        </Text>
      )}
      {shouldShowLocationField && (
        <>
          <InputLabel
            id={enableDailyQueueAssignedLocations ? 'accountLocations-label' : 'defaultAccountLocation-label'}
            fieldtype="required"
            htmlFor={enableDailyQueueAssignedLocations ? 'assigned-locations' : 'default-location'}
          >
            {enableDailyQueueAssignedLocations ? 'Locations' : 'Location'}
          </InputLabel>
          <Controller
            control={control}
            name={enableDailyQueueAssignedLocations ? 'accountLocations' : 'defaultAccountLocation'}
            render={({ field: { onChange } }) => (
              <SelectAccountLocation
                onChange={onChange}
                defaultValue={
                  enableDailyQueueAssignedLocations
                    ? getValues('accountLocations')
                    : getValues('defaultAccountLocation')
                }
                id={enableDailyQueueAssignedLocations ? 'assigned-locations' : 'default-location'}
                multiple={enableDailyQueueAssignedLocations}
                placeholder={enableDailyQueueAssignedLocations ? 'Select locations' : 'Select location'}
                error={enableDailyQueueAssignedLocations ? !!errors.accountLocations : !!errors.defaultAccountLocation}
                helperText={
                  enableDailyQueueAssignedLocations
                    ? (errors.accountLocations?.message as string)
                    : (errors.defaultAccountLocation?.message as string)
                }
              />
            )}
          />
        </>
      )}
    </>
  );
};
